import client from './client'
import { toRawListParams } from './paging'
import {
  TapApplication,
  TapWorkflowEvent,
  PaginatedResponse,
  TapApplicationListParams,
  TapAccessGrant,
  TapApplicationType,
  TapStage,
  TapStatusFlag,
  TapDecisionOutcome,
  TapSlaStatus,
} from '../types/index'

// ---------------------------------------------------------------------------
// Raw backend shapes (snake_case) and camelCase mappers.
//
// Bug #146: the backend returns snake_case (`position_title`, `reference_number`,
// `assigned_to`, `sla_status`, …) but the frontend TapApplication type and every
// column / detail-field renderer are camelCase. Passing the raw response through
// as `TapApplication` made every field resolve to `undefined`, so the table and
// detail pages rendered as empty rows. The fix is to map once here, matching
// the pattern used in `api/users.ts`.
// ---------------------------------------------------------------------------

// Feature #148: the backend's ApplicantSummary nested projection.
interface RawApplicantSummary {
  id: string
  first_name: string
  last_name: string
  email: string
  phone?: string | null
  // Feature #216: location in applicant summary.
  location?: string | null
}

interface RawTapApplication {
  id: string
  reference_number: string
  application_type: TapApplicationType
  position_title?: string | null
  vacancy_code?: string | null
  stage: TapStage
  decision_outcome?: TapDecisionOutcome | null
  status_flag?: TapStatusFlag | null
  assigned_to?: string | null
  // Bug #168: server-resolved assignee display name.
  assigned_to_display_name?: string | null
  received_at?: string | null
  summary?: string | null
  preferred_employment_type?: string | null
  // Feature #217: availability hours.
  availability?: string | null
  source_system: string
  source_id: string
  sla_status?: TapSlaStatus | null
  is_active: boolean
  created_at: string
  updated_at: string
  created_by?: string | null
  // Feature #148: optional nested applicant summary.
  applicant?: RawApplicantSummary | null
}

const mapApplication = (a: RawTapApplication): TapApplication => ({
  id: a.id,
  referenceNumber: a.reference_number,
  applicationType: a.application_type,
  positionTitle: a.position_title ?? undefined,
  vacancyCode: a.vacancy_code ?? undefined,
  stage: a.stage,
  decisionOutcome: a.decision_outcome ?? undefined,
  statusFlag: a.status_flag ?? undefined,
  assignedTo: a.assigned_to ?? undefined,
  assignedToDisplayName: a.assigned_to_display_name ?? undefined,
  receivedAt: a.received_at ?? undefined,
  summary: a.summary ?? undefined,
  preferredEmploymentType: a.preferred_employment_type ?? undefined,
  availability: a.availability ?? undefined,
  sourceSystem: a.source_system,
  sourceId: a.source_id,
  slaStatus: a.sla_status ?? undefined,
  isActive: Boolean(a.is_active),
  createdAt: a.created_at,
  updatedAt: a.updated_at,
  createdBy: a.created_by ?? undefined,
  applicant: a.applicant
    ? {
        id: a.applicant.id,
        firstName: a.applicant.first_name,
        lastName: a.applicant.last_name,
        email: a.applicant.email,
        phone: a.applicant.phone ?? undefined,
        location: a.applicant.location ?? undefined,
      }
    : undefined,
})

interface RawTapWorkflowEvent {
  id: string
  application_id: string
  event_type: string
  from_stage?: string | null
  to_stage?: string | null
  from_flag?: string | null
  to_flag?: string | null
  note?: string | null
  actor_user_id?: string | null
  // Bug #152: server resolves actor_user_id to a display name so the
  // timeline can render "Jane Smith changed stage" instead of a UUID.
  actor_display_name?: string | null
  occurred_at: string
}

const mapWorkflowEvent = (e: RawTapWorkflowEvent): TapWorkflowEvent => ({
  id: e.id,
  applicationId: e.application_id,
  eventType: e.event_type,
  fromStage: e.from_stage ?? undefined,
  toStage: e.to_stage ?? undefined,
  fromFlag: e.from_flag ?? undefined,
  toFlag: e.to_flag ?? undefined,
  note: e.note ?? undefined,
  actorUserId: e.actor_user_id ?? undefined,
  actorDisplayName: e.actor_display_name ?? undefined,
  occurredAt: e.occurred_at,
})

interface RawTapAccessGrant {
  id: string
  application_id: string
  user_id: string
  grant_type: string
  granted_by_user_id: string
  granted_at?: string | null
  expires_at?: string | null
  revoked_at?: string | null
  reason?: string | null
  is_active: boolean
}

const mapAccessGrant = (g: RawTapAccessGrant): TapAccessGrant => ({
  id: g.id,
  applicationId: g.application_id,
  userId: g.user_id,
  grantType: g.grant_type,
  grantedByUserId: g.granted_by_user_id,
  grantedAt: g.granted_at ?? '',
  expiresAt: g.expires_at ?? undefined,
  revokedAt: g.revoked_at ?? undefined,
  reason: g.reason ?? undefined,
  isActive: Boolean(g.is_active),
})

interface RawPaginatedApplications {
  items: RawTapApplication[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

/**
 * WI #337: the whole params object used to be forwarded verbatim. Every key on
 * `TapApplicationListParams` is camelCase and every query param the endpoint
 * binds is snake_case, so *none* of the filters reached the server — the list
 * always came back as the unfiltered first 20 rows and the page filtered that
 * page client-side.
 *
 * `sortBy` is passed through untouched: `list_applications` resolves it with
 * `getattr(TapApplication, sort_by)`, so the caller must supply a real column
 * name (see SORT_FIELDS in `pages/tap/ApplicationList.tsx`).
 */
const toRawApplicationParams = (
  params?: TapApplicationListParams,
): Record<string, unknown> | undefined => {
  if (!params) return undefined
  const raw: Record<string, unknown> = toRawListParams(params) || {}
  if (params.stage !== undefined) raw.stage = params.stage
  if (params.decisionOutcome !== undefined) raw.decision_outcome = params.decisionOutcome
  if (params.applicationType !== undefined) raw.application_type = params.applicationType
  if (params.vacancyCode !== undefined) raw.vacancy_code = params.vacancyCode
  if (params.statusFlag !== undefined) raw.status_flag = params.statusFlag
  if (params.assignedTo !== undefined) raw.assigned_to = params.assignedTo
  if (params.dateFrom !== undefined) raw.date_from = params.dateFrom
  if (params.dateTo !== undefined) raw.date_to = params.dateTo
  if (params.sharedWithMe !== undefined) raw.shared_with_me = params.sharedWithMe
  return raw
}

export const getApplications = async (
  params?: TapApplicationListParams,
): Promise<PaginatedResponse<TapApplication>> => {
  const response = await client.get<RawPaginatedApplications>('/tap/applications', {
    params: toRawApplicationParams(params),
  })
  const d = response.data
  return {
    items: (d.items || []).map(mapApplication),
    total: d.total,
    page: d.page,
    pageSize: d.page_size,
    pages: d.total_pages,
  }
}

export const getApplication = async (id: string): Promise<TapApplication> => {
  const response = await client.get<RawTapApplication>(`/tap/applications/${id}`)
  return mapApplication(response.data)
}

// Generate a stable client-side source_id for manually-created applications.
// Backend requires (source_system, source_id) to be unique and non-null; for
// records that originate from the UI (not Typeform / external sync) we stamp
// them with source_system='manual' and a UUID so the uniqueness constraint is
// satisfied without ambiguity.
const generateManualSourceId = (): string => {
  // crypto.randomUUID is available in modern browsers + jsdom. Fall back to
  // a timestamp-based value for older runtimes so the request never fails
  // just because we couldn't mint a UUID.
  const c = (globalThis as { crypto?: { randomUUID?: () => string } }).crypto
  if (c?.randomUUID) return `manual-${c.randomUUID()}`
  return `manual-${Date.now()}-${Math.random().toString(36).slice(2, 10)}`
}

// Feature #148: allow passing inline applicant info (first/last name + email,
// plus optional phone/location/linkedinUrl) so the New Application modal can
// collect the candidate's details in the same round trip. We use an
// omit-then-extend pattern rather than extending Partial<TapApplication>
// directly because the `applicant` payload shape on create (no id, optional
// phone/location/linkedinUrl) is different to the read-side `applicant`
// summary (id + name + email).
export type CreateApplicationInput = Omit<Partial<TapApplication>, 'applicant'> & {
  applicant?: {
    firstName: string
    lastName: string
    email: string
    phone?: string
    location?: string
    linkedinUrl?: string
  }
}

// Map the camelCase input the UI passes into the snake_case shape
// ApplicationCreate expects on the backend. Bug #140: sending camelCase keys
// (and omitting source_system/source_id entirely) was returning 422.
const toApplicationCreatePayload = (data: CreateApplicationInput): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    application_type: data.applicationType ?? 'JobApplication',
    position_title: data.positionTitle,
    vacancy_code: data.vacancyCode,
    stage: data.stage ?? 'Review',
    summary: data.summary,
    preferred_employment_type: data.preferredEmploymentType,
    source_system: data.sourceSystem ?? 'manual',
    source_id: data.sourceId ?? generateManualSourceId(),
  }
  if (data.applicant) {
    // Feature #148: snake_case applicant payload matches the backend
    // ApplicantCreatePayload schema exactly.
    payload.applicant = {
      first_name: data.applicant.firstName,
      last_name: data.applicant.lastName,
      email: data.applicant.email,
      phone: data.applicant.phone,
      location: data.applicant.location,
      linkedin_url: data.applicant.linkedinUrl,
    }
  }
  // Strip undefined values so Pydantic applies its own defaults rather than
  // erroring on `"foo": null` where the field is typed as Optional[str].
  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined) delete payload[key]
  })
  return payload
}

// Map a camelCase Partial<TapApplication> for PATCH updates. Same reasoning as
// the create mapper — the backend schema is snake_case.
// Bug #167: include application_type so recruiters can reclassify an
// application from the Summary tab (e.g. ExpressionOfInterest → JobApplication).
const toApplicationUpdatePayload = (data: Partial<TapApplication>): Record<string, unknown> => {
  const payload: Record<string, unknown> = {
    application_type: data.applicationType,
    position_title: data.positionTitle,
    vacancy_code: data.vacancyCode,
    stage: data.stage,
    decision_outcome: data.decisionOutcome,
    status_flag: data.statusFlag,
    assigned_to: data.assignedTo,
    summary: data.summary,
    preferred_employment_type: data.preferredEmploymentType,
    availability: data.availability,
  }
  Object.keys(payload).forEach((key) => {
    if (payload[key] === undefined) delete payload[key]
  })
  return payload
}

export const createApplication = async (data: CreateApplicationInput): Promise<TapApplication> => {
  const response = await client.post<RawTapApplication>('/tap/applications', toApplicationCreatePayload(data))
  return mapApplication(response.data)
}

export const updateApplication = async (id: string, data: Partial<TapApplication>): Promise<TapApplication> => {
  const response = await client.patch<RawTapApplication>(`/tap/applications/${id}`, toApplicationUpdatePayload(data))
  return mapApplication(response.data)
}

// Bug #167: PATCH the applicant attached to an application so recruiters can
// fix typos in name/email and add phone/location/linkedin from the Summary
// tab without leaving the detail page.
export interface ApplicantUpdateInput {
  firstName?: string
  lastName?: string
  email?: string
  phone?: string
  location?: string
  linkedinUrl?: string
}

export const updateApplicationApplicant = async (
  id: string,
  data: ApplicantUpdateInput,
): Promise<TapApplication> => {
  const payload: Record<string, unknown> = {
    first_name: data.firstName,
    last_name: data.lastName,
    email: data.email,
    phone: data.phone,
    location: data.location,
    linkedin_url: data.linkedinUrl,
  }
  Object.keys(payload).forEach((k) => {
    if (payload[k] === undefined) delete payload[k]
  })
  const response = await client.patch<RawTapApplication>(`/tap/applications/${id}/applicant`, payload)
  return mapApplication(response.data)
}

export const transitionApplication = async (id: string, toStage: string, note?: string): Promise<TapApplication> => {
  const response = await client.post<RawTapApplication>(`/tap/applications/${id}/transition`, { to_stage: toStage, note })
  return mapApplication(response.data)
}

export const flagApplication = async (id: string, statusFlag: string | null): Promise<TapApplication> => {
  const response = await client.post<RawTapApplication>(`/tap/applications/${id}/flag`, { status_flag: statusFlag })
  return mapApplication(response.data)
}

export const assignApplication = async (id: string, assignedTo: string | null): Promise<TapApplication> => {
  const response = await client.post<RawTapApplication>(`/tap/applications/${id}/assign`, { assigned_to: assignedTo })
  return mapApplication(response.data)
}

export const deleteApplication = async (id: string): Promise<void> => {
  await client.delete(`/tap/applications/${id}`)
}

// Bug #149: `/history` and `/access-grants` both return a paginated envelope
// (`{items, total, page, page_size, total_pages}`), not a bare array. Calling
// `.map` directly on response.data was throwing `((intermediate value).data ||
// []).map is not a function` and crashing the detail page load.
interface RawPaginatedWorkflowEvents {
  items: RawTapWorkflowEvent[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

interface RawPaginatedAccessGrants {
  items: RawTapAccessGrant[]
  total: number
  page: number
  page_size: number
  total_pages: number
}

export const getApplicationHistory = async (id: string): Promise<TapWorkflowEvent[]> => {
  const response = await client.get<RawPaginatedWorkflowEvents | RawTapWorkflowEvent[]>(
    `/tap/applications/${id}/history`,
  )
  // Be defensive: accept either shape so a backend that ever returns a bare
  // list (or an older cached response) doesn't re-break the detail page.
  const raw: RawTapWorkflowEvent[] = Array.isArray(response.data)
    ? response.data
    : response.data?.items || []
  return raw.map(mapWorkflowEvent)
}

// Access grants
export const getAccessGrants = async (applicationId: string): Promise<TapAccessGrant[]> => {
  const response = await client.get<RawPaginatedAccessGrants | RawTapAccessGrant[]>(
    `/tap/applications/${applicationId}/access-grants`,
  )
  const raw: RawTapAccessGrant[] = Array.isArray(response.data)
    ? response.data
    : response.data?.items || []
  return raw.map(mapAccessGrant)
}

export const createAccessGrant = async (
  applicationId: string,
  data: { user_id: string; grant_type: string; reason?: string },
): Promise<TapAccessGrant> => {
  const response = await client.post<RawTapAccessGrant>(`/tap/applications/${applicationId}/access-grants`, data)
  return mapAccessGrant(response.data)
}

export const revokeAccessGrant = async (applicationId: string, grantId: string): Promise<void> => {
  await client.delete(`/tap/applications/${applicationId}/access-grants/${grantId}`)
}

// ---------------------------------------------------------------------------
// Assignee picker (Bug #147).
//
// The Assign modal needs to show the list of users who can actually be
// assigned — i.e. people holding `manage_tap` (or `platform_admin`). Before,
// the modal was a freeform text box, the user could type anything, and the
// backend blew up with a 500 when the string wasn't a valid FK into lab_user.
// ---------------------------------------------------------------------------

export interface TapAssignee {
  id: string
  displayName: string
  email: string
  jobTitle?: string
  roles: string[]
}

interface RawTapAssignee {
  id: string
  display_name: string
  email: string
  job_title?: string | null
  roles: string[]
}

// Bug #195: added optional `role` param to filter by a specific role (e.g. 'tap_approver').
// WI #274 / #278: added optional `includeAllActive` flag. When true (and `role`
// is not set) the backend skips its TAP-role gate and returns every active
// LabUser. Use this for pickers that aren't actually about TAP assignment
// (Manager field on Assessment tab, Interview Attendees) where any staff
// member is a valid pick.
export const getTapAssignees = async (
  search?: string,
  role?: string,
  includeAllActive?: boolean,
): Promise<TapAssignee[]> => {
  const params: Record<string, string> = {}
  if (search) params.search = search
  if (role) params.role = role
  if (includeAllActive) params.include_all_active = 'true'
  const response = await client.get<RawTapAssignee[]>('/tap/assignees', {
    params: Object.keys(params).length > 0 ? params : undefined,
  })
  return (response.data || []).map((u) => ({
    id: u.id,
    displayName: u.display_name,
    email: u.email,
    jobTitle: u.job_title ?? undefined,
    roles: u.roles || [],
  }))
}


// ---------------------------------------------------------------------------
// Feature #201: Typeform submission viewer
// ---------------------------------------------------------------------------

export interface TypeformQA {
  question: string
  answer: string | null
  fieldType: string | null
}

export interface TypeformSubmission {
  submittedAt: string | null
  responseUrl: string | null
  items: TypeformQA[]
}

interface RawTypeformQA {
  question: string
  answer: string | null
  field_type: string | null
}

interface RawTypeformSubmission {
  submitted_at: string | null
  response_url: string | null
  items: RawTypeformQA[]
}

// ---------------------------------------------------------------------------
// Feature #224: prior applications by the same applicant
// ---------------------------------------------------------------------------

export interface PriorApplication {
  id: string
  referenceNumber: string
  stage: string
  decisionOutcome?: string
  receivedAt?: string
}

export interface PriorApplicationsResult {
  count: number
  applicantEmail?: string
  applicantFirstName?: string
  items: PriorApplication[]
}

interface RawPriorApplication {
  id: string
  reference_number: string
  stage: string
  decision_outcome?: string | null
  received_at?: string | null
}

interface RawPriorApplicationsResult {
  count: number
  applicant_email?: string | null
  applicant_first_name?: string | null
  items: RawPriorApplication[]
}

export const getPriorApplications = async (applicationId: string): Promise<PriorApplicationsResult> => {
  const response = await client.get<RawPriorApplicationsResult>(
    `/tap/applications/${applicationId}/prior-applications`,
  )
  const raw = response.data
  return {
    count: raw.count,
    applicantEmail: raw.applicant_email ?? undefined,
    applicantFirstName: raw.applicant_first_name ?? undefined,
    items: (raw.items || []).map((i) => ({
      id: i.id,
      referenceNumber: i.reference_number,
      stage: i.stage,
      decisionOutcome: i.decision_outcome ?? undefined,
      receivedAt: i.received_at ?? undefined,
    })),
  }
}


export const getTypeformSubmission = async (applicationId: string): Promise<TypeformSubmission> => {
  const response = await client.get<RawTypeformSubmission>(
    `/tap/applications/${applicationId}/typeform-submission`,
  )
  const raw = response.data
  return {
    submittedAt: raw.submitted_at,
    responseUrl: raw.response_url,
    items: (raw.items || []).map((item) => ({
      question: item.question,
      answer: item.answer,
      fieldType: item.field_type,
    })),
  }
}
